import { ShareButtonSecondary } from 'components/common-form/sidePanel/docToolbar/summary/ShareButtonSecondary';
import { moreAgressiveColorMapper } from 'components/PostTrade/ProductModification/components/ValidationErrors.utils';
import type { PlacedSidePanelProps } from 'components/secondaryMarket/limitOrder/sidePanel/placed/PlacedSidePanel.connect';
import { DetailsItem } from 'components/secondaryMarket/shared/SidePanel/DetailsItem';
import { DetailsItemValue } from 'components/secondaryMarket/shared/SidePanel/DetailsItemValue';
import { FlexColumnPanel } from 'components/secondaryMarket/shared/SidePanel/FlexColumnPanel';
import { LabelDetailsItem } from 'components/secondaryMarket/shared/SidePanel/LabelDetailsItem';
import { ConnectedOrderPlacedActionButtons } from 'components/secondaryMarket/shared/SidePanel/OrderPlacedActionButtons';
import { OrderPlacedHeader } from 'components/secondaryMarket/shared/SidePanel/OrderPlacedHeader';
import { TradeWay } from 'components/secondaryMarket/shared/SidePanel/TradeWay';
import { useFormattedOrderPlaced } from 'components/secondaryMarket/shared/SidePanel/useFormattedOrderPlaced';
import { MessagesPanel } from 'components/secondaryMarket/shared/status';
import { useErrorMessagesFormatter } from 'components/secondaryMarket/shared/status/messages/useErrorMessageFormatter';
import { PanelFooter } from 'components/secondaryMarket/shared/status/Panel';
import { useTradeWayLabel } from 'components/secondaryMarket/shared/status/tradeWay/useTradeWayLabel';
import { ListGroup } from 'components/shared/ListGroup';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { useSPIntl } from 'hooks/useIntl';
import { useUserNotification } from 'hooks/useUserNotification';
import { FC } from 'react';
import { HighlightedContent, Msg } from 'state/notifications/notification.model';

export const PlacedSidePanel: FC<PlacedSidePanelProps> = ({
  id,
  formId,
  isin,
  currency,
  fileCode,
  ric,
  onDone,
  priceType,
  closeTabWhenOrderIsPlacedActivated,
  validationErrors,
  tradeWay,
  settlementDate,
  cashAmount,
  quantity,
  tradeDate,
  clientLimit,
  traderLabel,
  execTime,
  hasOrderBlotter,
}) => {
  const { formatMessage, formatDisplayedDate, formatNumber } = useSPIntl();
  const { hint, summary } = useFormattedOrderPlaced({
    tradeWay,
    productIsin: isin,
    quantity,
    productRic: ric,
    fileCode,
    traderLabel,
    execTime,
    formattedCashAmountLabel: 'secondaryMarket.sidePanel.label.clientLimit',
  });

  const tradeWayLabel = useTradeWayLabel({ way: tradeWay! });
  const { info, warn } = useUserNotification();
  const messages = useErrorMessagesFormatter(validationErrors, moreAgressiveColorMapper);

  useEffectOnce(() => {
    if (!closeTabWhenOrderIsPlacedActivated) {
      return;
    }
    const title = formatMessage({ id: 'secondaryMarket.sidePanel.label.limitOrderPlaced' });
    const content: Msg[] = [{ content: tradeWayLabel!, severity: 'info' }];
    if (messages?.length > 0) {
      warn({
        title: { content: title, severity: 'warning' },
        content: [
          ...content,
          ...messages.map(m => ({ isHtml: true, content: m.htmlMessage, severity: 'warning' }) as HighlightedContent),
        ],
      });
    } else {
      info({
        title: { content: title, severity: 'info' },
        content,
      });
    }

    onDone();
  });

  return (
    <FlexColumnPanel>
      <OrderPlacedHeader hint={hint} summary={summary}>
        <div className="d-flex justify-content-between">
          <TradeWay tradeWay={tradeWay} />
          <ShareButtonSecondary
            tabId={formId}
            color="tradable"
            mustForceAdvancedMode={false}
            workspaceType="secondaryMarketQuotes"
            type="limit"
            summary={summary}
          />
        </div>
      </OrderPlacedHeader>
      <PanelFooter color="info" statusKey="secondaryMarket.sidePanel.label.limitOrderPlaced" icon="check_circle" />
      <MessagesPanel messagesToDisplay={messages} className="pt-2" e2e="side-panel-header" />
      <div className="flex-grow-1 m-4">
        <ListGroup>
          {id !== undefined && <LabelDetailsItem label="secondaryMarket.sidePanel.label.id" value={id} e2e="tradeId" />}
          {isin !== undefined && (
            <LabelDetailsItem label="secondaryMarket.sidePanel.label.isin" value={isin} e2e="isin" />
          )}
          {ric !== undefined && <LabelDetailsItem label="secondaryMarket.sidePanel.label.ric" value={ric} e2e="ric" />}
          {fileCode !== undefined && (
            <LabelDetailsItem label="secondaryMarket.sidePanel.label.fileCode" value={fileCode} e2e="fileCode" />
          )}
          {quantity !== undefined && (
            <DetailsItem label="secondaryMarket.sidePanel.label.quantity" e2e="quantity">
              {quantity}
            </DetailsItem>
          )}
          {cashAmount !== undefined && (
            <DetailsItem label="secondaryMarket.sidePanel.label.cashAmount" e2e="cashAmount">
              {formatNumber(cashAmount, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}{' '}
              <span className="ms-1">{currency}</span>
            </DetailsItem>
          )}
          {tradeWay !== undefined && (
            <DetailsItem label="secondaryMarket.sidePanel.label.tradeSide" e2e="tradeSide">
              {tradeWayLabel}
            </DetailsItem>
          )}

          {tradeDate !== undefined && (
            <DetailsItem label="secondaryMarket.sidePanel.label.tradeDate" e2e="tradeDate">
              {formatDisplayedDate(tradeDate)}
            </DetailsItem>
          )}
          <DetailsItemValue
            label="secondaryMarket.sidePanel.label.clientLimit"
            e2e="clientLimit"
            value={clientLimit}
            priceType={priceType}
            currency={currency}
            alreadyFormatted={false}
          />
          {settlementDate !== undefined && (
            <DetailsItem label="secondaryMarket.sidePanel.label.settlementDate" e2e="settlementDate">
              {formatDisplayedDate(settlementDate)}
            </DetailsItem>
          )}
        </ListGroup>
      </div>
      <ConnectedOrderPlacedActionButtons
        onDone={onDone}
        hasOrderBlotter={hasOrderBlotter}
        orderId={id}
        blotterRoute="/secondary-orders-blotter"
      />
    </FlexColumnPanel>
  );
};
PlacedSidePanel.displayName = 'TradedSidePanel';
